import PageLayout from '@/components/feature/PageLayout';
import { aboutContent } from '@/mocks/about';

export default function AboutPage() {
  const { brandIntro, milestones, reputation, contact, faqs } = aboutContent;

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于向日葵视频',
    description: '向日葵视频是国内领先的数字视频在线观看平台，提供91视频、97视频、51视频、久久视频等高清数字视频大全。',
    url: 'https://www.xiangrikuishipin.com/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '向日葵视频',
      url: 'https://www.xiangrikuishipin.com/',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '向日葵视频',
    url: 'https://www.xiangrikuishipin.com/',
    description: '向日葵视频是国内领先的数字视频在线观看平台。',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座15层',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      postalCode: '100022',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: contact.phone,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '09:00',
      closes: '21:00',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.xiangrikuishipin.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.xiangrikuishipin.com/about' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <PageLayout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <section className="w-full px-4 md:px-6 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于向日葵视频
            </h1>
            <p className="text-foreground-500 text-sm max-w-2xl mx-auto leading-relaxed">
              国内领先的数字视频在线观看平台，致力于为用户提供91视频、97视频、51视频、久久视频等高清数字视频大全
            </p>
          </div>

          {/* Brand Introduction */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              {brandIntro.title}
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
              {brandIntro.paragraphs.map((paragraph, idx) => (
                <p
                  key={idx}
                  className={`text-foreground-700 text-sm leading-relaxed ${
                    idx < brandIntro.paragraphs.length - 1 ? 'mb-4' : ''
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5 bg-primary-200"></div>
              <div className="space-y-8">
                {milestones.map((milestone, idx) => (
                  <div key={idx} className="relative pl-12 md:pl-16">
                    <div className="absolute left-2.5 md:left-4.5 top-1 w-3.5 h-3.5 rounded-full bg-primary-500 border-2 border-background-50"></div>
                    <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                      <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-2">
                        {milestone.year}
                      </span>
                      <h3 className="font-heading text-foreground-900 font-semibold mb-1">
                        {milestone.title}
                      </h3>
                      <p className="text-foreground-500 text-sm leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              {reputation.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {reputation.items.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-6 flex gap-4"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className={`${item.icon} text-lg text-accent-600`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-foreground-900 font-semibold mb-1.5">
                      {item.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              {contact.title}
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-primary-600"></i>
                  </span>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm mb-1">公司地址</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">{contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-phone-line text-lg text-primary-600"></i>
                  </span>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm mb-1">联系电话</h4>
                    <p className="text-foreground-500 text-xs">{contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </span>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm mb-1">电子邮箱</h4>
                    <a href={`mailto:${contact.email}`} className="text-foreground-500 text-xs hover:text-primary-600 transition-colors">
                      {contact.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-time-line text-lg text-primary-600"></i>
                  </span>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm mb-1">工作时间</h4>
                    <p className="text-foreground-500 text-xs">{contact.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-foreground-900 text-sm">
                    <span>{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </section>
    </PageLayout>
  );
}